import { getToday } from "../utils";
import { SISTEMAS } from "../constants";

// ═══════════════════════════════════════════════════════════════
// ADESÃO DO CLIENTE — o CRM é a porta dos módulos
// ═══════════════════════════════════════════════════════════════
//
// Todo cliente nasce aqui: prospecção comercial, licitação ganha, indicação ou
// vindo de um parceiro. O que o CRM faz é registrar a adesão e acompanhar até
// ele estar ATIVO dentro do módulo contratado.
//
// O que o CRM NÃO faz, de propósito: plano, preço, fatura, cobrança, veículo,
// rastreador. Tudo isso já existe dentro de cada módulo, com regra própria —
// no rastreamento, o `calculo.ts` é declarado fonte única da conta. Copiar
// qualquer dessas regras para cá criaria uma segunda verdade sobre dinheiro,
// que é o pior lugar possível para duas versões discordarem.
//
// A relação aqui é a mesma do fornecedor: o CRM manda o cadastro, o módulo
// responde com o id dele, e a partir daí o CRM só ESPELHA.

export const ORIGENS = [
  { id: "comercial", label: "Prospecção comercial", cor: "#7c3aed", dica: "Veio do funil: contato, apresentação, proposta" },
  { id: "licitacao", label: "Licitação ganha", cor: "#0d9488", dica: "Contrato público — registre órgão e número do processo" },
  { id: "indicacao", label: "Indicação", cor: "#2563eb", dica: "Chegou por indicação de cliente ou parceiro" },
  { id: "parceiro", label: "Trazido por parceiro da rede", cor: "#f59e0b", dica: "Uma oficina ou posto da rede trouxe o cliente" },
];

export const rotuloOrigem = (id) => ORIGENS.find((o) => o.id === id)?.label || "—";
export const corOrigem = (id) => ORIGENS.find((o) => o.id === id)?.cor || "#64748b";

/**
 * O que cada módulo precisa saber para criar o cliente lá dentro. É o mínimo
 * — o resto o próprio módulo pergunta, porque é dono da regra.
 */
export const EXIGE_POR_SISTEMA = {
  manutencao: [
    { campo: "veiculos", label: "Quantos veículos", tipo: "numero", dica: "Serve para dimensionar o atendimento" },
  ],
  combustivel: [
    { campo: "veiculos", label: "Quantos veículos", tipo: "numero" },
    { campo: "limiteMensal", label: "Limite mensal combinado (R$)", tipo: "numero", opcional: true },
  ],
  rastreamento: [
    { campo: "veiculos", label: "Quantos veículos vão receber rastreador", tipo: "numero" },
    { campo: "plano", label: "Plano contratado", tipo: "texto", dica: "O preço e os limites vêm do módulo de rastreamento" },
  ],
};

export const camposDoSistema = (id) => EXIGE_POR_SISTEMA[id] || [];

/** O que falta preencher para a adesão poder ser registrada. */
export function pendenciasDaAdesao(dados = {}) {
  const faltam = [];
  if (!String(dados.company || "").trim()) faltam.push("Nome da empresa");
  if (!String(dados.cnpj || "").replace(/\D/g, "")) faltam.push("CNPJ");
  if (!String(dados.contact || "").trim()) faltam.push("Responsável");
  if (!String(dados.phone || "").trim() && !String(dados.email || "").trim()) faltam.push("Telefone ou e-mail");
  if (!dados.origem) faltam.push("Como este cliente chegou");
  if (!(dados.systems || []).length) faltam.push("Ao menos um módulo contratado");
  if (dados.origem === "licitacao") {
    if (!String(dados.licitacao?.orgao || "").trim()) faltam.push("Órgão da licitação");
    if (!String(dados.licitacao?.numero || "").trim()) faltam.push("Número do processo");
  }
  for (const sis of dados.systems || []) {
    for (const c of camposDoSistema(sis)) {
      if (c.opcional) continue;
      if (!String(dados.adesao?.[sis]?.[c.campo] ?? "").trim()) {
        faltam.push(`${c.label} (${SISTEMAS.find((s) => s.id === sis)?.label || sis})`);
      }
    }
  }
  return faltam;
}

/** O cliente já existe naquele módulo? Lido do que o módulo respondeu. */
export function situacaoNoSistema(cliente = {}, sistema) {
  const sync = cliente.integracaoSync && typeof cliente.integracaoSync === "object" ? cliente.integracaoSync : {};
  const s = sync[sistema];
  if (!s || typeof s !== "object") return { estado: "nao_enviado" };
  if (s.client_id || s.clientId || s.id) {
    return {
      estado: "ativo",
      id: s.client_id || s.clientId || s.id,
      ts: s.ts,
      // O que o módulo disse que ainda falta lá dentro (plano, veículo,
      // rastreador instalado). "Cadastrado" não é o mesmo que "operando".
      pendencias: Array.isArray(s.pendencias) ? s.pendencias : [],
      operando: !!s.operando,
    };
  }
  if (s.ok === false) return { estado: "erro", erro: s.erro || s.message || "não foi possível cadastrar", ts: s.ts };
  return { estado: "enviado", ts: s.ts };
}

/** O quadro geral: em quantos módulos ele já está de pé. */
export function resumoAtivacao(cliente = {}) {
  const contratados = cliente.systems || [];
  const estados = contratados.map((s) => ({ sistema: s, ...situacaoNoSistema(cliente, s) }));
  const ativos = estados.filter((e) => e.estado === "ativo");
  return {
    contratados: contratados.length,
    ativos: ativos.length,
    completo: contratados.length > 0 && ativos.length === contratados.length,
    estados,
  };
}

/** Registra a adesão no cliente, com trilha. */
export function aplicarAdesao(cliente, dados, { autor = "" } = {}, hoje = getToday()) {
  const sistemas = (dados.systems || []).map((s) => SISTEMAS.find((x) => x.id === s)?.label || s).join(", ");
  return {
    ...cliente,
    ...dados,
    stage: "ganho",
    adesao: { ...(dados.adesao || {}), registradaEm: hoje, registradaPor: autor },
    historico: [
      ...(Array.isArray(cliente.historico) ? cliente.historico : []),
      {
        ts: new Date().toISOString(), acao: "adesao", autor,
        detalhe: `Adesão registrada — ${rotuloOrigem(dados.origem)}. Módulos: ${sistemas}.`,
      },
    ],
    stageHistory: [
      ...(Array.isArray(cliente.stageHistory) ? cliente.stageHistory : []),
      { from: cliente.stage || null, to: "ganho", date: hoje, autor, tipo: "adesao", note: `Adesão registrada (${rotuloOrigem(dados.origem)})` },
    ],
  };
}

/**
 * As tarefas de ativação — uma por módulo contratado.
 *
 * A tarefa é sobre o cliente estar USANDO o módulo, não sobre usar o botão do
 * CRM. Cadastrar direto lá dentro resolve a tarefa do mesmo jeito; o botão
 * existe só para não redigitar o que já foi digitado aqui.
 */
export function tarefasDeAtivacao(cliente, { userId } = {}) {
  return (cliente.systems || []).map((sis) => ({
    sistema: sis,
    titulo: `Ativar no ${SISTEMAS.find((s) => s.id === sis)?.label || sis} — ${cliente.company}`,
    notas: sis === "rastreamento"
      ? `Instalar rastreador em ${cliente.adesao?.rastreamento?.veiculos || "?"} veículo(s). Plano: ${cliente.adesao?.rastreamento?.plano || "a definir"} (o plano se define no módulo).`
      : `Criar o cliente no módulo e liberar acesso. Dá para fazer direto lá — o botão do CRM é atalho.`,
    userId,
  }));
}
